import { redirectToLogin } from "../logout";
import { decryptToken } from "../../lib/decryptAuth";
import { getToken, setToken } from "../../services/storageService";
import {
  captureLoginSelectionFromCallback,
  enrichLoginSelection,
  evaluateLoginApplicationAccess,
  resolveAssignedApplicationsForSession,
  resolveCrossOriginBounceUrl,
  type LoginSelectedApplication,
} from "../../utils/matchesAssignedApplicationOrigin";
import { fetchSessionByGatewayEndpoint } from "./authSession.api";
import {
  persistAuthMeResponse,
  persistSessionUser,
  readCachedSessionUser,
} from "./authSession.storage";
import { markPostLoginGracePeriod } from "./authSession.grace";
import type { AuthSessionUser } from "./authSession.types";
import {
  isSsoPrefetchRequest,
  notifySsoPrefetchComplete,
} from "./ssoPrefetch";

function redirectToLoginUnlessPrefetch(): void {
  if (isSsoPrefetchRequest()) {
    notifySsoPrefetchComplete();
    return;
  }
  redirectToLogin();
}

type CallbackRedirectParams = {
  bearer?: string;
  endpoint?: string;
};

export type LoginRedirectResult = {
  user: AuthSessionUser;
  accessDenied: boolean;
  selection: LoginSelectedApplication | null;
};

let loginFromRedirectPromise: Promise<LoginRedirectResult> | null = null;
let loginFromRedirectKey: string | null = null;

function formatAuthFailure(err: unknown): string {
  if (err && typeof err === "object") {
    const ax = err as {
      message?: string;
      response?: { status?: number; data?: unknown };
      code?: string;
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    const detail =
      data && typeof data === "object"
        ? JSON.stringify(data)
        : data != null
          ? String(data)
          : "";
    const parts = [
      ax.message || "request failed",
      status != null ? `status=${status}` : "",
      ax.code ? `code=${ax.code}` : "",
      detail ? `body=${detail}` : "",
    ].filter(Boolean);
    return parts.join(" | ");
  }
  return err instanceof Error ? err.message : String(err);
}

function callbackSessionKey(): string {
  const params = readCallbackParamsFromUrl();
  return `${params.bearer ?? ""}|${params.endpoint ?? ""}`;
}

function readCallbackParamsFromUrl(): CallbackRedirectParams {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const endpoint = params.get("endpoint")?.trim() || undefined;
  const tokenParam = params.get("token")?.trim();
  const authParam = params.get("auth")?.trim();

  return {
    bearer: tokenParam || authParam || undefined,
    endpoint,
  };
}

async function resolveBearerFromRedirectUrl(
  params: CallbackRedirectParams,
): Promise<string | undefined> {
  if (typeof window === "undefined") return undefined;

  const authParam = params.bearer;
  if (!authParam) return undefined;

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("auth")) {
    const secret = (process.env.REACT_APP_AUTH_SECRET ?? "").trim();
    if (secret) {
      try {
        return await decryptToken(authParam, secret);
      } catch {
        return undefined;
      }
    }
  }

  return authParam;
}

function stripSensitiveParamsFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const hadSensitive =
    params.has("auth") ||
    params.has("token") ||
    params.has("refresh_token") ||
    params.has("endpoint") ||
    params.has("app_id") ||
    params.has("app_url") ||
    params.has("app_name");
  if (!hadSensitive) return;

  params.delete("auth");
  params.delete("token");
  params.delete("refresh_token");
  params.delete("endpoint");
  params.delete("app_id");
  params.delete("app_url");
  params.delete("app_name");

  const newSearch = params.toString();
  const newUrl =
    window.location.pathname +
    (newSearch ? `?${newSearch}` : "") +
    window.location.hash;
  window.history.replaceState({}, "", newUrl);
}

function evaluateApplicationAccess(
  user: AuthSessionUser,
  selection: LoginSelectedApplication | null,
): boolean {
  return evaluateLoginApplicationAccess(user, selection);
}

function buildCrossOriginCallbackRedirectUrl(
  targetAppUrl: string,
  bearer: string,
  endpoint: string,
  selection: LoginSelectedApplication | null,
): string | null {
  try {
    const dest = new URL(targetAppUrl);
    const callbackPath = getAuthCallbackPath();
    dest.pathname = callbackPath;
    dest.search = "";
    dest.searchParams.set("token", bearer);
    dest.searchParams.set("endpoint", endpoint);

    const params = new URLSearchParams(window.location.search);
    const refreshToken = params.get("refresh_token")?.trim();
    if (refreshToken) {
      dest.searchParams.set("refresh_token", refreshToken);
    }

    const returnTo = readLoginReturnTo();
    if (returnTo) {
      dest.searchParams.set("return_to", returnTo);
    }
    if (selection?.id) {
      dest.searchParams.set("app_id", selection.id);
    }
    if (selection?.url) {
      dest.searchParams.set("app_url", selection.url);
    }
    if (selection?.name) {
      dest.searchParams.set("app_name", selection.name);
    }

    return dest.toString();
  } catch {
    return null;
  }
}

function hasFreshLoginCallback(): boolean {
  const params = readCallbackParamsFromUrl();
  return Boolean(params.bearer && params.endpoint);
}

function tryReturnEstablishedSession(): LoginRedirectResult | null {
  if (hasFreshLoginCallback()) {
    return null;
  }

  const cachedUser = readCachedSessionUser();
  if (cachedUser && getToken()) {
    stripSensitiveParamsFromUrl();
    const selection = captureLoginSelectionFromCallback();
    const enrichedSelection = enrichLoginSelection(
      selection,
      resolveAssignedApplicationsForSession(cachedUser),
    );
    return {
      user: cachedUser,
      accessDenied: !evaluateApplicationAccess(cachedUser, enrichedSelection),
      selection: enrichedSelection,
    };
  }
  return null;
}

async function establishLoginFromRedirect(): Promise<LoginRedirectResult> {
  markPostLoginGracePeriod();

  const existingSession = tryReturnEstablishedSession();
  if (existingSession) {
    return existingSession;
  }

  const callbackParams = readCallbackParamsFromUrl();
  const bearer = await resolveBearerFromRedirectUrl(callbackParams);
  const endpoint = callbackParams.endpoint;

  if (!bearer || !endpoint) {
    const sessionAfterRace = tryReturnEstablishedSession();
    if (sessionAfterRace) {
      return sessionAfterRace;
    }
    redirectToLoginUnlessPrefetch();
    throw new Error("Missing login token or session endpoint");
  }

  setToken(bearer);

  try {
    const selection = captureLoginSelectionFromCallback();
    const { raw, user } = await fetchSessionByGatewayEndpoint(endpoint, { bearer });
    const enrichedSelection = enrichLoginSelection(
      selection,
      resolveAssignedApplicationsForSession(user),
    );

    const bounceTarget = resolveCrossOriginBounceUrl(user, enrichedSelection);
    if (bounceTarget) {
      const redirectUrl = buildCrossOriginCallbackRedirectUrl(
        bounceTarget,
        bearer,
        endpoint,
        enrichedSelection,
      );
      if (redirectUrl) {
        window.location.replace(redirectUrl);
        return new Promise<LoginRedirectResult>(() => {
          /* navigation in progress */
        });
      }
    }

    persistAuthMeResponse(raw);
    persistSessionUser(user);
    markPostLoginGracePeriod();
    stripSensitiveParamsFromUrl();

    const accessDenied = !evaluateApplicationAccess(user, enrichedSelection);
    return { user, accessDenied, selection: enrichedSelection };
  } catch (err) {
    const detail = formatAuthFailure(err);
    console.error("[auth] establishLoginFromRedirect failed:", detail, err);
    if (!hasFreshLoginCallback()) {
      const sessionAfterFailure = tryReturnEstablishedSession();
      if (sessionAfterFailure) {
        return sessionAfterFailure;
      }
    }
    redirectToLoginUnlessPrefetch();
    throw new Error(`Session could not be established (${detail})`);
  }
}

export function completeLoginFromRedirect(): Promise<LoginRedirectResult> {
  const key = isLoginCallbackWithSessionParams() ? callbackSessionKey() : "";
  if (isLoginCallbackWithSessionParams() && key !== loginFromRedirectKey) {
    loginFromRedirectPromise = null;
    loginFromRedirectKey = key;
  }
  if (!loginFromRedirectPromise) {
    loginFromRedirectPromise = establishLoginFromRedirect();
  }
  return loginFromRedirectPromise;
}

export function readLoginReturnTo(): string {
  const params = new URLSearchParams(window.location.search);
  const returnTo = (params.get("return_to") ?? "/").trim();
  if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return "/";
  }
  return returnTo;
}

export function getAuthCallbackPath(): string {
  const configured = (
    process.env.REACT_APP_AUTH_CALLBACK_PATH ?? "/auth/callback"
  ).trim();
  return configured.startsWith("/") ? configured : `/${configured}`;
}

export function isAuthCallbackPath(
  pathname: string = window.location.pathname,
): boolean {
  const callbackPath = getAuthCallbackPath();
  return pathname === callbackPath || pathname.endsWith(callbackPath);
}

export function callbackUrlHasToken(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("token") || params.has("auth");
}

export function isLoginCallbackWithSessionParams(): boolean {
  return isAuthCallbackPath() && callbackUrlHasToken();
}

export const APPLICATION_NOT_ASSIGNED_PATH = "/application-not-assigned";

export function isApplicationNotAssignedPath(
  pathname: string = window.location.pathname,
): boolean {
  return (
    pathname === APPLICATION_NOT_ASSIGNED_PATH ||
    pathname.endsWith(APPLICATION_NOT_ASSIGNED_PATH)
  );
}
